const CREATIVE_FILE_TYPE_LABELS = {
  activity_list: '활동명 중심 자료',
  student_work: '학생 활동 기록',
  mixed: '활동명과 학생 기록이 섞인 자료',
};
const CREATIVE_FILE_BLOCK_START = '[파일에서 정리한 창체 활동]';
const CREATIVE_FILE_BLOCK_END = '[파일에서 정리한 창체 활동 끝]';

const toText = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.map(toText).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    return Object.values(value).map(toText).filter(Boolean).join(', ');
  }
  return String(value).trim();
};

export const classifyCreativeInputProfile = (value) => {
  const text = toText(value);
  return (
    text.includes('[학생 역할]')
    || text.includes('[직접 한 활동]')
    || text.includes('[활동 과정]')
    || text.includes('[산출물]')
    || text.includes('[구체 근거]')
    || text.includes('[학생]')
  )
    ? 'detailed_evidence'
    : 'activity_list';
};

const toList = (value) => {
  if (Array.isArray(value)) return value.map(toText).filter(Boolean);
  const text = toText(value);
  return text ? [text] : [];
};

const appendLine = (lines, label, value) => {
  const text = toText(value);
  if (text) lines.push(`[${label}] ${text}`);
};

const buildActivityDetailText = (detail = {}, index = 0) => {
  const lines = [];
  const activityName = toText(detail.activity_name || detail.name);
  lines.push(activityName ? `[활동 ${index + 1}] ${activityName}` : `[활동 ${index + 1}]`);
  appendLine(lines, '학생 역할', detail.student_role || detail.role);

  const actions = toList(detail.actions || detail.observations);
  if (actions.length > 0) {
    lines.push('[직접 한 활동]');
    actions.forEach((action) => lines.push(`- ${action}`));
  }

  appendLine(lines, '활동 과정', detail.process);
  appendLine(lines, '산출물', detail.output || detail.outcome);

  const evidence = toList(detail.evidence);
  if (evidence.length > 0) {
    lines.push(`[구체 근거] ${evidence.join(', ')}`);
  }
  appendLine(lines, '세부 내용', detail.description);
  return lines.join('\n').trim();
};

export const buildCreativeAnalysisDraft = (data = {}) => {
  const lines = [];
  const fileType = data.file_type || (
    Array.isArray(data.activity_details) && data.activity_details.length > 0
      ? 'student_work'
      : 'activity_list'
  );
  const fileTypeLabel = CREATIVE_FILE_TYPE_LABELS[fileType] || CREATIVE_FILE_TYPE_LABELS.mixed;
  const activities = toText(data.activities)
    || toList(data.activities_list).join(', ');

  appendLine(lines, '자료 유형', fileTypeLabel);
  appendLine(lines, '활동명', activities);

  const details = Array.isArray(data.activity_details) ? data.activity_details : [];
  details.forEach((detail, index) => {
    const detailText = buildActivityDetailText(detail, index);
    if (detailText) lines.push(detailText);
  });

  const summary = toText(data.summary);
  if (summary && !lines.some((line) => line.includes(summary))) {
    appendLine(lines, '자료 요약', summary);
  }

  return lines.filter(Boolean).join('\n\n').trim();
};

export const buildCreativeStudentDraft = (student = {}, index = 0) => {
  const studentName = toText(student.name) || `학생 ${index + 1}`;
  const body = buildCreativeAnalysisDraft({
    ...student,
    file_type: 'student_work',
  });
  return [`[학생] ${studentName}`, body].filter(Boolean).join('\n\n').trim();
};

export const getCreativeAnalysisSummaryItems = (data = {}) => {
  const fileTypeLabel = CREATIVE_FILE_TYPE_LABELS[data.file_type] || '창체 활동 자료';
  const activities = toText(data.activities)
    || toList(data.activities_list).join(', ');
  const detailsCount = Array.isArray(data.activity_details) ? data.activity_details.length : 0;

  return [
    { label: '자료 유형', value: fileTypeLabel },
    activities ? { label: '감지한 활동', value: activities } : null,
    detailsCount > 0 ? { label: '구체 활동', value: `${detailsCount}개` } : null,
    data.summary ? { label: '요약', value: toText(data.summary) } : null,
  ].filter(Boolean);
};

export const mergeCreativeAnalysisWithPrompt = (
  existingPrompt,
  analysisText,
  previousAnalysisText = ''
) => {
  const existing = toText(existingPrompt);
  const incoming = toText(analysisText);
  const previous = toText(previousAnalysisText);
  const wrappedIncoming = incoming
    ? `${CREATIVE_FILE_BLOCK_START}\n${incoming}\n${CREATIVE_FILE_BLOCK_END}`
    : '';

  if (!incoming) return existing;
  if (!existing) return wrappedIncoming;
  if (existing.includes(wrappedIncoming)) return existing;
  if (existing.includes(CREATIVE_FILE_BLOCK_START) && existing.includes(CREATIVE_FILE_BLOCK_END)) {
    const startIndex = existing.indexOf(CREATIVE_FILE_BLOCK_START);
    const endIndex = existing.indexOf(CREATIVE_FILE_BLOCK_END, startIndex);
    if (endIndex >= startIndex) {
      const afterEnd = endIndex + CREATIVE_FILE_BLOCK_END.length;
      return `${existing.slice(0, startIndex)}${wrappedIncoming}${existing.slice(afterEnd)}`
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    }
  }
  if (existing.includes(incoming)) return existing;
  if (previous && existing.includes(previous)) {
    return existing
      .replace(previous, wrappedIncoming)
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
  return `${existing}\n\n${wrappedIncoming}`;
};

export const removeCreativeAnalysisFromPrompt = (prompt, previousAnalysisText = '') => {
  const existing = toText(prompt);
  const startIndex = existing.indexOf(CREATIVE_FILE_BLOCK_START);
  const endIndex = existing.indexOf(CREATIVE_FILE_BLOCK_END, startIndex);
  if (startIndex < 0 || endIndex < startIndex) {
    const previous = toText(previousAnalysisText);
    return previous && existing.includes(previous)
      ? existing.replace(previous, '').replace(/\n{3,}/g, '\n\n').trim()
      : existing;
  }
  return `${existing.slice(0, startIndex)}${existing.slice(endIndex + CREATIVE_FILE_BLOCK_END.length)}`
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const buildCreativeUploadedFileMetadata = (analysis = {}, fileMeta = {}) => {
  const analyzedData = analysis.analyzed_data || {};
  const warnings = [
    ...(Array.isArray(analysis.warnings) ? analysis.warnings : []),
    ...(analysis.warning ? [analysis.warning] : []),
  ].filter(Boolean);

  return {
    parsed_length: Number(fileMeta.parsed_length || 0),
    file_analysis_type: analyzedData.file_type || '',
    has_concrete_details: Boolean(analyzedData.has_concrete_details),
    multi_student: Boolean(analyzedData.multi_student),
    warning_count: new Set(warnings).size,
  };
};

